/**
 * Bloco #63: importação de pacientes a partir de CSV exportado de outro software.
 *
 * Lógica pura (sem React) para ser testável: leitura do CSV com detecção de
 * separador, mapeamento de colunas para os campos do Allodos, validação linha a
 * linha e detecção de duplicidades com o cadastro atual.
 */

export type ImportField =
  | 'name' | 'dob' | 'phone' | 'email' | 'cpf' | 'cep' | 'address'
  | 'emergencyContact' | 'emergencyPhone' | 'motherName' | 'fatherName'
  | 'mainComplaint' | 'medications';

export const IMPORT_FIELD_LABELS: Record<ImportField, string> = {
  name: 'Nome completo (obrigatório)',
  dob: 'Data de nascimento',
  phone: 'Telefone / WhatsApp',
  email: 'E-mail',
  cpf: 'CPF',
  cep: 'CEP',
  address: 'Endereço',
  emergencyContact: 'Contato de emergência',
  emergencyPhone: 'Telefone de emergência',
  motherName: 'Nome da mãe',
  fatherName: 'Nome do pai',
  mainComplaint: 'Queixa principal',
  medications: 'Medicações em uso',
};

export const IMPORT_FIELDS = Object.keys(IMPORT_FIELD_LABELS) as ImportField[];

/** Apelidos comuns de cabeçalho (pt/en, com ou sem acento) → campo do Allodos. */
const ALIASES: Record<ImportField, string[]> = {
  name: ['nome', 'nome completo', 'paciente', 'nome do paciente', 'name', 'full name', 'patient', 'cliente'],
  dob: ['nascimento', 'data de nascimento', 'dt nascimento', 'data nasc', 'dob', 'birth', 'birthdate', 'date of birth', 'birthday', 'aniversario'],
  phone: ['telefone', 'celular', 'whatsapp', 'fone', 'tel', 'phone', 'mobile', 'contato'],
  email: ['email', 'e-mail', 'mail'],
  cpf: ['cpf', 'documento', 'cpf do paciente'],
  cep: ['cep', 'zip', 'zipcode', 'postal code'],
  address: ['endereco', 'endereço completo', 'logradouro', 'address', 'rua'],
  emergencyContact: ['contato de emergencia', 'emergencia', 'responsavel', 'emergency contact', 'nome do responsavel'],
  emergencyPhone: ['telefone de emergencia', 'tel emergencia', 'emergency phone', 'telefone do responsavel'],
  motherName: ['mae', 'nome da mae', 'mother', 'mother name'],
  fatherName: ['pai', 'nome do pai', 'father', 'father name'],
  mainComplaint: ['queixa', 'queixa principal', 'motivo', 'demanda', 'complaint', 'main complaint', 'observacoes', 'observacao'],
  medications: ['medicacoes', 'medicacao', 'medicamentos', 'remedios', 'medications', 'medication'],
};

export const normalizeHeader = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[_\-./]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Sugere o campo de destino para um cabeçalho do CSV (ou null se não reconhecer). */
export function suggestField(header: string, taken: Set<ImportField> = new Set()): ImportField | null {
  const h = normalizeHeader(header);
  if (!h) return null;
  for (const field of IMPORT_FIELDS) {
    if (taken.has(field)) continue;
    if (ALIASES[field].some((alias) => normalizeHeader(alias) === h)) return field;
  }
  for (const field of IMPORT_FIELDS) {
    if (taken.has(field)) continue;
    if (ALIASES[field].some((alias) => h.includes(normalizeHeader(alias)))) return field;
  }
  return null;
}

export type ParsedCsv = { headers: string[]; rows: string[][]; delimiter: string };

export function detectDelimiter(firstLine: string): string {
  const candidates = [';', ',', '\t', '|'];
  let best = ';';
  let bestCount = -1;
  for (const d of candidates) {
    const count = firstLine.split(d).length - 1;
    if (count > bestCount) {
      best = d;
      bestCount = count;
    }
  }
  return best;
}

/** Leitor CSV com suporte a aspas, BOM, CRLF e quebras de linha dentro de aspas. */
export function parseCsv(text: string): ParsedCsv {
  const clean = text.replace(/^﻿/, '');
  const firstLine = clean.split(/\r?\n/).find((l) => l.trim()) ?? '';
  const delimiter = detectDelimiter(firstLine);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];
    if (quoted) {
      if (ch === '"') {
        if (clean[i + 1] === '"') { cell += '"'; i++; } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') { quoted = true; continue; }
    if (ch === delimiter) { row.push(cell); cell = ''; continue; }
    if (ch === '\r') continue;
    if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; continue; }
    cell += ch;
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  const nonEmpty = rows.filter((r) => r.some((c) => c.trim() !== ''));
  const headers = (nonEmpty.shift() ?? []).map((h) => h.trim());
  return { headers, rows: nonEmpty.map((r) => headers.map((_, idx) => (r[idx] ?? '').trim())), delimiter };
}

export type ColumnMapping = Record<number, ImportField | null>;

/** Mapeamento inicial: cada coluna recebe no máximo um campo e cada campo uma coluna. */
export function autoMap(headers: string[]): ColumnMapping {
  const taken = new Set<ImportField>();
  const mapping: ColumnMapping = {};
  headers.forEach((h, idx) => {
    const field = suggestField(h, taken);
    mapping[idx] = field;
    if (field) taken.add(field);
  });
  return mapping;
}

export type ImportRecord = Partial<Record<ImportField, string>> & { name: string };

export type ImportRow = {
  line: number;
  record: ImportRecord;
  errors: string[];
  /** Possível duplicidade com um paciente já cadastrado (nome + motivo). */
  duplicateOf?: { id: string; name: string; reason: string };
  /** Linha anterior do mesmo arquivo que parece ser a mesma pessoa. */
  duplicateInFile?: number;
};

const onlyDigits = (v: string) => v.replace(/\D/g, '');

/** Aceita dd/mm/aaaa, dd-mm-aaaa, aaaa-mm-dd e aaaa/mm/dd; devolve aaaa-mm-dd ou null. */
export function normalizeDate(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  let m = v.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
  let y: number, mo: number, d: number;
  if (m) { d = +m[1]; mo = +m[2]; y = +m[3]; }
  else {
    m = v.match(/^(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})/);
    if (!m) return null;
    y = +m[1]; mo = +m[2]; d = +m[3];
  }
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) return null;
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export type ExistingPatient = { id: string; name: string; cpf?: string; email?: string; dob?: string };

const personKey = (name: string, dob?: string) => `${normalizeHeader(name)}|${dob ?? ''}`;

/** Monta os registros a partir do CSV e do mapeamento, validando e marcando duplicidades. */
export function buildImportRows(parsed: ParsedCsv, mapping: ColumnMapping, existing: ExistingPatient[]): ImportRow[] {
  const byCpf = new Map<string, ExistingPatient>();
  const byEmail = new Map<string, ExistingPatient>();
  const byPerson = new Map<string, ExistingPatient>();
  const byName = new Map<string, ExistingPatient>();
  for (const p of existing) {
    const cpf = p.cpf ? onlyDigits(p.cpf) : '';
    if (cpf) byCpf.set(cpf, p);
    if (p.email) byEmail.set(p.email.trim().toLowerCase(), p);
    byPerson.set(personKey(p.name, p.dob ? normalizeDate(p.dob) ?? p.dob : undefined), p);
    byName.set(normalizeHeader(p.name), p);
  }
  const seenInFile = new Map<string, number>();

  return parsed.rows.map((cells, idx) => {
    const line = idx + 2; // +1 cabeçalho, +1 base 1
    const record: ImportRecord = { name: '' };
    const errors: string[] = [];
    Object.entries(mapping).forEach(([col, field]) => {
      if (!field) return;
      const value = cells[Number(col)] ?? '';
      if (value) record[field] = value;
    });
    record.name = (record.name ?? '').trim();
    if (!record.name) errors.push('Nome completo ausente.');

    if (record.dob) {
      const iso = normalizeDate(record.dob);
      if (!iso) errors.push(`Data de nascimento inválida: "${record.dob}" (use dd/mm/aaaa).`);
      else record.dob = iso;
    }
    if (record.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(record.email)) errors.push(`E-mail inválido: "${record.email}".`);
    if (record.cpf) {
      const digits = onlyDigits(record.cpf);
      if (digits.length !== 11) errors.push(`CPF inválido: "${record.cpf}".`);
      else record.cpf = digits;
    }

    const row: ImportRow = { line, record, errors };
    if (record.name) {
      const cpf = record.cpf ?? '';
      const email = (record.email ?? '').toLowerCase();
      const key = personKey(record.name, record.dob);
      const dupCpf = cpf ? byCpf.get(cpf) : undefined;
      const dupEmail = email ? byEmail.get(email) : undefined;
      const dupPerson = byPerson.get(key);
      // Mesmo nome (ainda que sem data) é "possível duplicidade": o usuário decide.
      const dupName = byName.get(normalizeHeader(record.name));
      const dup = dupCpf ?? dupEmail ?? dupPerson ?? dupName;
      if (dup) {
        const reason = dupCpf ? 'mesmo CPF' : dupEmail ? 'mesmo e-mail' : dupPerson && record.dob ? 'mesmo nome e data de nascimento' : 'mesmo nome';
        row.duplicateOf = { id: dup.id, name: dup.name, reason };
      }
      const fileKeys = [cpf && `cpf:${cpf}`, email && `email:${email}`, `name:${normalizeHeader(record.name)}`].filter(Boolean) as string[];
      const prev = fileKeys.map((k) => seenInFile.get(k)).find((v) => v !== undefined);
      if (prev !== undefined) row.duplicateInFile = prev;
      fileKeys.forEach((k) => { if (!seenInFile.has(k)) seenInFile.set(k, line); });
    }
    return row;
  });
}

export type ImportSummary = { total: number; valid: number; invalid: number; duplicates: number; unmappedColumns: string[] };

export function summarizeImport(rows: ImportRow[], headers: string[], mapping: ColumnMapping): ImportSummary {
  return {
    total: rows.length,
    valid: rows.filter((r) => r.errors.length === 0).length,
    invalid: rows.filter((r) => r.errors.length > 0).length,
    duplicates: rows.filter((r) => r.duplicateOf || r.duplicateInFile !== undefined).length,
    unmappedColumns: headers.filter((_, idx) => !mapping[idx]),
  };
}
